/**
 * Helper to convert a string to Title Case with Turkish locale support.
 */
const toTurkishTitleCase = (str: string): string => {
  if (!str) return "";
  const lower = str.toLocaleLowerCase("tr");
  return lower
    .split(" ")
    .map((word) => {
      if (word.length === 0) return "";
      return word.charAt(0).toLocaleUpperCase("tr") + word.slice(1);
    })
    .join(" ");
};

const formatCategoryTitles = () => {
  const elements = document.querySelectorAll(".mobile-subcat-all-text");
  elements.forEach((el) => {
    if (el.textContent) {
      el.textContent = toTurkishTitleCase(el.textContent);
    }
  });
};

export const initMobileMenuOffcanvas = (): void => {
  const wrapper = document.querySelector<HTMLElement>(
    "#mobile-menu-offcanvas-wrapper",
  );
  if (!wrapper) return;

  const toggleButtons = document.querySelectorAll<HTMLButtonElement>(
    ".mobile-menu-toggle",
  );
  const panel = wrapper.querySelector<HTMLElement>(
    ".mobile-menu-offcanvas__panel",
  );
  const overlay = wrapper.querySelector<HTMLElement>(
    ".mobile-menu-offcanvas__overlay",
  );
  const closeButton = panel?.querySelector<HTMLButtonElement>(
    ".mobile-menu-offcanvas__close-button",
  );
  const scrollContainer = panel?.querySelector<HTMLElement>(".offcanvas-body");

  if (!panel || !overlay || !closeButton || !scrollContainer) return;

  // --- NAVIGATION STACK ---
  // Tracks the IDs of opened panels.
  // Initial state: just the root categories view.
  let panelHistory: string[] = ["drilldown-root"];

  const drilldownRoot = panel.querySelector<HTMLElement>("#drilldown-root");

  /**
   * Slides into a new sub-panel and adds it to history.
   */
  const navigateToPanel = (targetId: string) => {
    const currentPanelId = panelHistory[panelHistory.length - 1];
    const currentPanelEl = document.getElementById(currentPanelId);
    const targetPanelEl = document.getElementById(targetId);

    if (!targetPanelEl || !currentPanelEl) return;

    // 1. Move current panel to the left (parallax effect)
    currentPanelEl.classList.remove("is-current");
    currentPanelEl.style.transform = "translateX(-20%)";

    // 2. Prepare and slide in the target panel from the right
    targetPanelEl.classList.add("is-active");
    // Force a reflow for the transition
    requestAnimationFrame(() => {
      targetPanelEl.style.transform = "translateX(0)";
    });

    // 3. Update history
    panelHistory.push(targetId);

    // 4. Reset scroll so user starts at the top of the new list
    scrollContainer.scrollTop = 0;
  };

  /**
   * Slides back to the previous panel in history.
   */
  const navigateBack = () => {
    if (panelHistory.length < 2) return;

    // 1. Get the current (top) panel and the one beneath it
    const currentPanelId = panelHistory.pop()!;
    const previousPanelId = panelHistory[panelHistory.length - 1];

    const currentPanelEl = document.getElementById(currentPanelId);
    const previousPanelEl = document.getElementById(previousPanelId);

    if (!currentPanelEl || !previousPanelEl) return;

    // 2. Slide current panel out to the right
    currentPanelEl.style.transform = "translateX(100%)";

    // 3. Bring previous panel back to center from the left
    previousPanelEl.classList.add("is-current"); // Only the root uses this class for display logic
    previousPanelEl.style.transform = "translateX(0)";

    // 4. Cleanup: Hide the panel we just left after transition
    setTimeout(() => {
      if (!panelHistory.includes(currentPanelId)) {
        currentPanelEl.classList.remove("is-active");
      }
    }, 300);
  };

  /**
   * Resets everything to the root view. Called when menu closes.
   */
  const resetDrilldown = () => {
    if (!drilldownRoot) return;

    // Reset all panels to their starting positions
    const allPanels = panel.querySelectorAll<HTMLElement>(
      ".mobile-drilldown-panel",
    );
    allPanels.forEach((p) => {
      p.classList.remove("is-active");
      p.style.transform = "translateX(100%)";
    });

    // Reset Root
    drilldownRoot.classList.add("is-current");
    drilldownRoot.style.transform = "translateX(0)";

    // Clear stack
    panelHistory = ["drilldown-root"];
  };

  // --- CORE UI HANDLERS ---

  const openMenu = () => {
    document.body.classList.add("mobile-menu-offcanvas-open");
    wrapper.classList.add("is-open");
    wrapper.setAttribute("aria-hidden", "false");
    toggleButtons.forEach((btn) => btn.setAttribute("aria-expanded", "true"));
  };

  const closeMenu = () => {
    document.body.classList.remove("mobile-menu-offcanvas-open");
    wrapper.classList.remove("is-open");
    wrapper.setAttribute("aria-hidden", "true");
    toggleButtons.forEach((btn) => btn.setAttribute("aria-expanded", "false"));
    setTimeout(resetDrilldown, 400); // Reset after closing transition finishes
  };

  const initSubmenuToggling = () => {
    const submenuToggles = panel.querySelectorAll<HTMLButtonElement>(
      ".mobile-menu-offcanvas__nav-list .mobile-menu-offcanvas__submenu-toggle",
    );
    submenuToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const parentItem = toggle.closest(".menu-item-has-children");
        if (parentItem) {
          const isExpanded = parentItem.classList.toggle("is-open");
          toggle.setAttribute("aria-expanded", String(isExpanded));
        }
      });
    });
  };

  const initTabs = () => {
    const tabButtons = panel.querySelectorAll<HTMLButtonElement>(
      ".mobile-menu-offcanvas__tab-btn",
    );
    const tabPanes = panel.querySelectorAll<HTMLElement>(
      ".mobile-menu-offcanvas__tab-pane",
    );

    tabButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        tabButtons.forEach((b) => b.classList.remove("is-active"));
        tabPanes.forEach((p) => p.classList.remove("is-active"));
        btn.classList.add("is-active");
        const targetId = btn.dataset.target;
        if (targetId) {
          document.getElementById(targetId)?.classList.add("is-active");
        }
      });
    });
  };

  const initDrilldown = () => {
    // 1. Root Level Triggers (Category Cards)
    panel
      .querySelectorAll<HTMLButtonElement>(".mobile-cat-card__trigger")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const targetId = btn.dataset.targetPanel;
          if (targetId) navigateToPanel(targetId);
        });
      });

    // 2. Sub-level Triggers (List Items with children)
    panel
      .querySelectorAll<HTMLButtonElement>(".mobile-subcat-item--trigger")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const targetId = btn.dataset.targetPanel;
          if (targetId) navigateToPanel(targetId);
        });
      });

    // 3. Back Buttons (Common for all panels)
    panel
      .querySelectorAll<HTMLButtonElement>(".mobile-drilldown-back-btn")
      .forEach((btn) => {
        btn.addEventListener("click", navigateBack);
      });
  };

  // Bind Events
  toggleButtons.forEach((btn) => btn.addEventListener("click", openMenu));
  closeButton.addEventListener("click", closeMenu);
  overlay.addEventListener("click", closeMenu);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && wrapper.classList.contains("is-open")) {
      closeMenu();
    }
  });

  initSubmenuToggling();
  initTabs();
  initDrilldown();
  formatCategoryTitles();

  console.log("[MobileMenu] Stack-based navigation initialized.");
};
